<template>
  <b-modal
    v-model="visible"
    size="xl"
    :title="title"
    dialog-class="person-detail-dialog"
    content-class="person-detail-content"
    body-class="person-detail-body"
    hide-footer
  >
    <template v-if="detail">
      <div class="person-editor">
        <b-input
          :value="editName"
          :placeholder="$t('people_name_placeholder')"
          @input="$emit('update:edit-name', $event)"
        />
        <b-button
          v-if="detail.status === 'candidate'"
          class="person-save-draft"
          variant="outline-primary"
          @click="$emit('save')"
        >
          {{ $t('people_save_draft') }}
        </b-button>
        <b-button
          v-if="detail.status === 'candidate'"
          class="person-confirm"
          variant="primary"
          :disabled="!canConfirm"
          @click="$emit('confirm')"
        >
          {{ $t('people_confirm_person') }}
        </b-button>
        <b-button v-else class="person-save" variant="outline-primary" @click="$emit('save')">
          {{ $t('apply') }}
        </b-button>
        <b-button
          v-if="detail.status === 'confirmed'"
          class="person-make-draft"
          variant="outline-warning"
          @click="$emit('make-draft')"
        >
          {{ $t('people_move_to_drafts') }}
        </b-button>
        <b-select
          v-if="detail.status === 'candidate'"
          v-model="mergeTarget"
          class="person-merge-into"
          :disabled="confirmedMoveTargets.length === 0 && draftMoveTargets.length === 0"
          @change="mergeIntoPerson"
        >
          <option :value="null" disabled>{{ $t('people_merge_into') }}</option>
          <optgroup v-if="confirmedMoveTargets.length" :label="$t('people_confirmed')">
            <option v-for="person in confirmedMoveTargets" :key="person.id" :value="person.id">
              {{ person.name }} ({{ person.face_count }} {{ $t('people_photos') }})
            </option>
          </optgroup>
          <optgroup v-if="draftMoveTargets.length" :label="$t('people_named_unconfirmed')">
            <option v-for="person in draftMoveTargets" :key="person.id" :value="person.id">
              {{ person.name }} ({{ person.face_count }} {{ $t('people_photos') }})
            </option>
          </optgroup>
        </b-select>
        <b-button variant="outline-light" @click="$emit('toggle-hidden')">
          {{ detail.status === 'hidden' ? $t('people_unhide') : $t('people_hide') }}
        </b-button>
      </div>

      <PersonVideoGenerator
        :person="detail"
        :job="videoJob"
        :download-url="videoDownloadUrl"
        @generate="$emit('generate-video', $event)"
        @cancel="$emit('cancel-video')"
      />

      <div v-if="selectedFaces.length" class="face-actions">
        <b-select
          :value="moveTarget"
          size="sm"
          @input="$emit('update:move-target', $event)"
        >
          <option :value="null">{{ $t('people_detach') }}</option>
          <optgroup v-if="confirmedMoveTargets.length" :label="$t('people_confirmed')">
            <option v-for="person in confirmedMoveTargets" :key="person.id" :value="person.id">
              {{ person.name }}
            </option>
          </optgroup>
          <optgroup v-if="draftMoveTargets.length" :label="$t('people_drafts')">
            <option v-for="person in draftMoveTargets" :key="person.id" :value="person.id">
              {{ person.name }}
            </option>
          </optgroup>
        </b-select>
        <b-button size="sm" variant="primary" @click="$emit('move-faces')">
          {{ $t('people_move') }}
        </b-button>
        <b-button size="sm" variant="outline-warning" @click="$emit('split-faces')">
          {{ $t('people_split') }}
        </b-button>
        <b-button size="sm" variant="outline-danger" @click="$emit('ignore-faces')">
          {{ $t('people_ignore') }}
        </b-button>
      </div>

      <div ref="facesScroll" class="person-faces-scroll" @scroll.passive="maybeLoadMore">
        <div class="faces-grid">
          <article
            v-for="face in detail.faces.items"
            :key="face.id"
            class="face-tile"
            :class="{ selected: selectedFaces.includes(face.id) }"
          >
            <button class="face-select" type="button" @click="$emit('toggle-face', face.id)">
              <img :src="face.crop_url" alt="" />
              <span>{{ face.media && (face.media.date || face.media.filename) }}</span>
            </button>
            <button
              class="face-source"
              type="button"
              :aria-label="$t('people_source_photo')"
              :title="$t('people_source_photo')"
              @click.stop="$emit('show-source', face)"
            >
              <i class="bi bi-image" />
            </button>
          </article>
        </div>
        <div v-if="hasMore" class="people-infinite-loader" aria-live="polite">
          <b-spinner v-if="loadingFaces" small />
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import PersonVideoGenerator from './PersonVideoGenerator.vue'

export default {
  components: { PersonVideoGenerator },
  props: {
    value: { type: Boolean, required: true },
    detail: { type: Object, default: null },
    editName: { type: String, default: '' },
    selectedFaces: { type: Array, required: true },
    loadingFaces: { type: Boolean, default: false },
    moveTarget: { type: [Number, String], default: null },
    moveTargets: { type: Array, required: true },
    videoJob: { type: Object, default: null },
    videoDownloadUrl: { type: String, default: '' },
  },
  computed: {
    canConfirm() {
      return Boolean((this.editName || '').trim())
    },
    confirmedMoveTargets() {
      return this.orderedMoveTargets('confirmed')
    },
    draftMoveTargets() {
      return this.orderedMoveTargets('candidate')
    },
    hasMore() {
      return Boolean(this.detail && this.detail.faces.total > this.detail.faces.items.length)
    },
    visible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    title() {
      return this.detail ? (this.detail.name || this.$t('people_unnamed')) : ''
    },
  },
  data() {
    return {
      loadRequested: false,
      mergeTarget: null,
    }
  },
  watch: {
    loadingFaces(loading, wasLoading) {
      if (wasLoading && !loading) {
        this.loadRequested = false
      }
    },
    value(visible) {
      if (!visible) this.loadRequested = false
      else this.$nextTick(() => this.maybeLoadMore())
    },
  },
  methods: {
    orderedMoveTargets(status) {
      return this.moveTargets
        .filter(person => person.status === status && person.name && person.name.trim())
        .sort((first, second) => first.name.localeCompare(second.name))
    },
    mergeIntoPerson(personId) {
      if (personId == null) return
      this.$emit('merge-into-person', personId)
      this.mergeTarget = null
    },
    maybeLoadMore(event) {
      const container = event?.currentTarget || this.$refs.facesScroll
      if (!container || !this.hasMore || this.loadingFaces || this.loadRequested) return
      const remaining = container.scrollHeight - container.scrollTop - container.clientHeight
      if (remaining > 240) return
      this.loadRequested = true
      this.$emit('load-more')
    },
  },
}
</script>

<style>
.person-detail-dialog.modal-dialog {
  height: calc(100vh - 2rem);
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.person-detail-content.modal-content {
  max-height: 100%;
}

.person-detail-body.modal-body {
  display: flex;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
}

</style>

<style scoped>
.person-editor,
.face-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: .5rem;
  margin-bottom: 1rem;
}

.person-editor .form-control {
  flex: 1;
}

.person-merge-into {
  max-width: 300px;
}

.face-actions .custom-select {
  max-width: 250px;
}

.faces-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.person-faces-scroll {
  flex: 1 1 auto;
  min-height: 0;
  padding-right: .25rem;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.face-tile {
  position: relative;
  overflow: hidden;
  border: 3px solid transparent;
  border-radius: 9px;
  background: rgba(127, 127, 127, .08);
  color: inherit;
}

.face-tile.selected {
  border-color: var(--highlight-color);
}

.face-select {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: inherit;
}

.face-select img {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
}

.face-select span {
  display: block;
  overflow: hidden;
  padding: .4rem;
  font-size: .7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.face-source {
  position: absolute;
  top: .4rem;
  right: .4rem;
  display: grid;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, .45);
  border-radius: 50%;
  background: rgba(0, 0, 0, .68);
  color: white;
  opacity: .82;
  place-items: center;
  transition: background-color .15s ease, opacity .15s ease, transform .15s ease;
}

.face-source:hover,
.face-source:focus {
  background: var(--highlight-color);
  opacity: 1;
  transform: scale(1.08);
}

.people-infinite-loader {
  height: 3rem;
  padding: 1rem;
  text-align: center;
}
</style>
